
import { defineComponent, ref, watch, onMounted, computed } from "vue";
import useMedia from "@/composables/useMedia";
import { useRouter } from "vue-router";
import { imageUrlBuilder } from "@/services/sanity";
import Grid from "@moonlight/vue3/components/Grid.vue";
import Row from "@moonlight/vue3/components/Row.vue";
import Col from "@moonlight/vue3/components/Col.vue";
import Button from "@moonlight/vue3/components/Button.vue";
import { trans } from "@/globals/translator";
import TrailerModal from "@/components/common/TrailerModal.vue";
import RichText from "@/components/common/RichText.vue";
import { formatDateLong } from "@/utils/dateTimeFormatter";
import Loading from "@/components/common/Loading.vue";
import { useHead } from "@vueuse/head";
import ImagePoster from "@/components/common/ImagePoster.vue";
import RelatedArticles from "@/components/media/RelatedArticles.vue";
import useAuthUser from "@/composables/useAuthUser";
import WatchButton from "@/components/media/WatchButton.vue";

export default defineComponent({
  components: {
    Grid,
    Row,
    Col,
    Button,
    TrailerModal,
    RichText,
    Loading,
    ImagePoster,
    RelatedArticles,
    WatchButton,
  },
  props: {
    slug: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const router = useRouter();
    const { fetchMedia, media, requestStateMedia } = useMedia();
    const isTrailerModalOpen = ref(false);
    const { authUser, isFetchingAuthUser } = useAuthUser();

    onMounted(async () => {
      await fetchMedia(props.slug);
      if (!media.value) {
        router.replace("/404");
      }
    });

    watch(
      () => props.slug,
      async () => {
        await fetchMedia(props.slug);
        if (!media.value) {
          router.replace("/404");
        }
      }
    );

    const normalizedTrailerUrl = computed(() => {
      const url = media.value?.trailerUrl?.trim();
      if (!url) return null;
      if (url.indexOf("https://extras.filmin.com/") === 0) {
        return url.replace(
          "https://extras.filmin.com/",
          "https://static.filmin.es/resources/extras/"
        );
      } else if (url.indexOf("/data/static/resources/extras/") === 0) {
        return url.replace(
          "/data/static/resources/extras/",
          "https://static.filmin.es/resources/extras/"
        );
      }
      return url;
    });

    useHead({
      title: computed(() => media.value?.title as string),
      meta: [
        {
          name: `description`,
          content: computed(
            () => media.value?.body?.[0]?.children?.[0]?.text as string
          ),
        },
      ],
    });

    const currentUrl = window.location.href;

    const isMediaInPressMediaIds = computed(() => {
      return authUser.value?.pressMediasIds?.includes(
        media.value?.mediaId as number
      );
    });

    return {
      media,
      normalizedTrailerUrl,
      imageUrlBuilder,
      trans,
      isTrailerModalOpen,
      formatDateLong,
      requestStateMedia,
      isFetchingAuthUser,
      authUser,
      currentUrl,
      isMediaInPressMediaIds,
    };
  },
});
